import { FC, useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "use-debounce";
import { useForm } from "react-hook-form";

import { Branch, CostCenter } from "~/application/types";
import { branchService } from "~/application/usecases";
import { Box } from "~/components/Box";
import { Button } from "~/components/Button";
import { Container } from "~/components/Container";
import { DialogBody } from "~/components/Dialog";
import { Form } from "~/components/Form/Form";
import { FieldLabel, FormControl } from "~/components/FormControl";
import { FormDialog } from "~/components/FormDialog";
import { Col, Row } from "~/components/Grid";
import { SvgKey } from "~/components/Icon/icons";
import { TextInput } from "~/components/Input";
import { MultiSelectWrap } from "~/components/Input/MultiSelectWrap";
import { Text } from "~/components/Text";
import { H5 } from "~/components/Typography";
import { QueryKeys } from "~/constants";
import { CostCenterDialogProps } from "./types";
import { CostCenterSchema, costCenterSchema, defaultFormManager } from "./utils";
import { useUser } from "~/presentation/core/contexts/UserContext";

export const CostCenterDialog: FC<CostCenterDialogProps> = ({
  data,
  customerId,
  onCloseClick,
  onSubmit,
  customer,
}) => {
  const { contexts } = useUser();

  const formData = useForm<CostCenterSchema>({
    defaultValues: defaultFormManager(data, contexts, customer),
    resolver: yupResolver(costCenterSchema),
  });

  const [searchBranch, setSearchBranch] = useState("");
  const [searchDebounced] = useDebounce(searchBranch, 500);

  const {
    watch,
    control,
    formState: { isSubmitting, errors },
    handleSubmit,
    setValue,
  } = formData;

  const isNew = !data;

  const { data: branches, isLoading: isLoadingBranches } = useQuery(
    [QueryKeys.CUSTOMER_BRANCHES, customerId, searchDebounced],
    async () => {
      return await branchService.find({
        page: 1,
        customerId: customerId,
        withHeadquarters: true,
        name: searchDebounced,
      });
    },
  );

  const onBranchChange = (selectedBranches: Branch[]) => {
    if (selectedBranches.find((b) => b.name === "Desmarcar todos")) {
      setValue("markAllBranches", false);
      return;
    }

    if (selectedBranches.find((b) => b.name === "Marcar todos")) {
      setValue("markAllBranches", true);
      return;
    }
  };

  useEffect(() => {
    if (!branches?.data || !customerId || watch("branches")?.length) return;

    const headquarters = branches.data.find((b) => b.uuid === customerId);

    if (headquarters && isNew) {
      setValue("branches", [headquarters]);
    }
  }, [branches?.data, customerId, setValue, watch]);

  const handleOnSubmit = (data: CostCenterSchema) => {
    onSubmit({
      ...data,
      customerId: customerId,
      belongsToHeadquarters: data.branches?.some((b) => b.uuid === customerId),
      branches: data.branches?.filter((b) => b.uuid !== customerId),
    } as CostCenter);
  };

  return (
    <Container
      data-cy="cost-center-dialog"
      size={{ "@initial": "8", "@mxlg": "4" }}
      fixed
      css={{ overflow: "visible" }}
    >
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <FormDialog
          css={{ overflow: "visible" }}
          title={isNew ? "Novo centro de custo" : "Editar centro de custo"}
          negativeButton={
            <Button variant="tertiary" onClick={onCloseClick}>
              <Text>Cancelar</Text>
            </Button>
          }
          positiveButton={
            <Button disabled={isSubmitting} type="submit" data-cy="cost-center-submit">
              <Text>{isNew ? "Adicionar" : "Aplicar"}</Text>
            </Button>
          }
          onClickDismissButton={onCloseClick}
        >
          <DialogBody css={{ p: "$6", maxHeight: "70vh" }}>
            <Box css={{ mb: "$6" }}>
              <H5>Informações básicas</H5>
            </Box>

            <Row gap="6" css={{ "@mxlg": { flexDirection: "column" } }}>
              <Col sz={{ "@lg": "6" }}>
                <FormControl name="costCenterCode" control={control} required>
                  <FieldLabel tooltip="Código de identificação do centro de custo para integração com outros sistemas.">
                    Integração externa
                  </FieldLabel>
                  <TextInput
                    data-cy="cost-center-code"
                    leftIcon={SvgKey}
                    placeholder="Número do ID"
                  />
                  {errors.costCenterCode && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {errors.costCenterCode.message}
                    </Text>
                  )}
                </FormControl>
              </Col>

              <Col sz={{ "@lg": "6" }}>
                <FormControl name="name" control={control} required>
                  <FieldLabel>Nome do centro de custo</FieldLabel>
                  <TextInput
                    data-cy="cost-center-name"
                    placeholder="Digite o nome do centro de custo"
                  />
                  {errors.name && (
                    <Text variant="error-base" size="2" css={{ mt: "$2", fontWeight: "bold" }}>
                      {errors.name.message}
                    </Text>
                  )}
                </FormControl>
              </Col>

              <Col sz={12}>
                <FormControl name="branches" control={control}>
                  <FieldLabel>Empresas</FieldLabel>
                  <MultiSelectWrap
                    dataCy="cost-center-branches"
                    withMarkAll
                    portalled
                    setOptions={(branches) => setValue("branches", branches)}
                    onChange={onBranchChange}
                    isLoading={isLoadingBranches}
                    options={branches?.data ?? []}
                    getOptionLabel={(branch) => branch.name}
                    getOptionValue={(branch) => branch.uuid}
                    placeholder="Selecione as filiais"
                    size="sm"
                    onInputChange={setSearchBranch}
                  />
                </FormControl>
              </Col>
            </Row>
          </DialogBody>
        </FormDialog>
      </Form>
    </Container>
  );
};

CostCenterDialog.displayName = "CostCenterDialog";
